import { z } from 'zod'

export const TRAINER_CONTRACT_VERSION = '1.0.0' as const

const nullableString = z.string().nullable()
const nullableNumber = z.number().finite().nullable()

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerEventReadQuerySchema = z.object({
  orgId: optionalQueryString,
  search: optionalQueryString,
  cursor: optionalQueryString,
  limit: optionalQueryString,
})

export const trainerEventSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  createdByUserId: z.string().min(1),
  name: z.string(),
  description: nullableString,
  eventDate: nullableString,
  participantsNumber: nullableString,
  courseType: nullableString,
  courseTypeId: nullableString,
  budget: nullableNumber,
  organisationDetails: nullableString,
  city: nullableString,
  country: nullableString,
  postalCode: nullableString,
  venueName: nullableString,
  startTime: nullableNumber,
  endTime: nullableNumber,
  channelCode: nullableString,
  certificateFormId: nullableString,
  certificateUrl: nullableString,
  groupWorkFormId: nullableString,
  groupWorkUrl: nullableString,
  sourceTrainingCentreRequestId: nullableString,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const trainerReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
})

export const trainerEventReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([z.literal('trainer_events'), z.literal('trainer_channels')]),
  projection: z.literal('trainer-event-catalog'),
  rawPayloadReturned: z.literal(false),
})

export const trainerEventListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  events: z.array(trainerEventSummarySchema),
  pagination: z.object({
    limit: z.number().int().positive(),
    resultCount: z.number().int().nonnegative(),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
  eventEvidence: trainerEventReadEvidenceSchema,
  sideEffects: trainerReadSideEffectsSchema,
})

export const trainerWriteSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
})

export const trainerEventDetailResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  event: trainerEventSummarySchema,
  idempotentReplay: z.boolean().optional(),
  sideEffects: trainerWriteSideEffectsSchema,
})

export const trainerEventRosterRecordSchema = z.object({
  id: z.string().min(1),
  displayName: z.string(),
  source: z.enum(['certificate_submission', 'event_invoice_customer']),
  confidence: z.enum(['high', 'medium']),
  metadata: z.object({
    workshopName: nullableString,
    organization: nullableString,
    designation: nullableString,
    homeCity: nullableString,
    invoiceEventName: nullableString,
  }),
})

export const trainerEventRosterResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  roster: z.object({
    eventId: z.string().min(1),
    orgId: z.string().min(1),
    archivedParticipantCount: z.number().int().nonnegative().nullable(),
    records: z.array(trainerEventRosterRecordSchema),
    unresolved: z.object({
      count: z.number().int().nonnegative(),
      reason: z.enum(['archived_count_exceeds_trusted_rows', 'no_archived_count', 'none']),
    }),
  }),
  rosterEvidence: z.object({
    sourceOfTruth: z.literal('DB1'),
    tablesRead: z.tuple([
      z.literal('trainer_events'),
      z.literal('trainer_channels'),
      z.literal('trainer_certificate_submissions'),
      z.literal('trainer_invoices'),
    ]),
    projection: z.literal('trainer-event-roster'),
    rawEmailReturned: z.literal(false),
    rawPayloadReturned: z.literal(false),
  }),
  sideEffects: trainerReadSideEffectsSchema,
})

export const TRAINER_ERROR_CODES = [
  'TR_VALIDATION_FAILED',
  'TR_PERMISSION_DENIED',
  'TR_RATE_LIMITED',
  'TR_AUTH_UNAVAILABLE',
  'TR_MEMBERSHIP_UNAVAILABLE',
  'TR_LEGACY_AUTH_REMOVED',
  'TR_DOMAIN_UNAVAILABLE',
  'TR_CLIENT_VERIFICATION_REQUIRED',
  'TR_IDEMPOTENCY_KEY_REUSED',
  'TR_CHANNEL_CODE_AMBIGUOUS',
  'TR_CHANNEL_OWNERSHIP_MISMATCH',
  'TR_PUBLIC_SHARE_REVOKED',
] as const

export const trainerErrorEnvelopeSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  error: z.object({
    code: z.enum(TRAINER_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().regex(/^TR-[A-Z0-9]{8}$/i),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().uuid().optional(),
  }),
})

export type TrainerEventReadQuery = z.infer<typeof trainerEventReadQuerySchema>
export type TrainerEventSummary = z.infer<typeof trainerEventSummarySchema>
export type TrainerReadSideEffects = z.infer<typeof trainerReadSideEffectsSchema>
export type TrainerEventReadEvidence = z.infer<typeof trainerEventReadEvidenceSchema>
export type TrainerEventListResponse = z.infer<typeof trainerEventListResponseSchema>
export type TrainerWriteSideEffects = z.infer<typeof trainerWriteSideEffectsSchema>
export type TrainerEventDetailResponse = z.infer<typeof trainerEventDetailResponseSchema>
export type TrainerEventRosterRecord = z.infer<typeof trainerEventRosterRecordSchema>
export type TrainerEventRosterResponse = z.infer<typeof trainerEventRosterResponseSchema>
export type TrainerErrorCode = (typeof TRAINER_ERROR_CODES)[number]
export type TrainerErrorEnvelope = z.infer<typeof trainerErrorEnvelopeSchema>
